import { useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { refreshAccessToken } from '@/api/auth.api';
import { checkTeam } from '@/api/stores.api';
import { RequireAuthType } from '@/interfaces/component/requireAuth.interface';
import { useOrganizationTeamStore } from '@/stores/organizationTeam.store';
import { useToastStore } from '@/stores/toast.store';
import { getToken } from '@/utils/auth.util';
import FlexBox from '@/components/FlexBox';
import HospitalMenu from '@/components/HospitalMenu';
import RequestAuthenticationModal from '@/components/modal/RequestAuthenticationModal';
import AlreadyConfirmedModal from '@/views/organization/components/AlreadyConfirmedModal';
import CreateOrganizationModal from '@/views/organization/components/CreateOrganizationModal';
import OwnershipVerificationModal from '@/views/organization/components/OwnershipVerificationModal';
import TeamRegistrationModal from '@/views/organization/components/TeamRegistrationModal';

interface StoreAuthHandlerProps {
  currentAppStore: {
    storeId: number;
    storeName: string;
    storeDetail?: string;
    channels?: { channelId: string }[];
  } | null;
  onLoginSuccess?: () => void;
  redirectToReviews?: boolean;
  totalReviewCount?: number;
  renderRightSection?: () => React.ReactNode;
  isMobile?: boolean;
}

const StoreAuthHandler = ({ currentAppStore, onLoginSuccess, redirectToReviews = false, totalReviewCount = 0, renderRightSection, isMobile: propIsMobile }: StoreAuthHandlerProps) => {
  const { teams, organizations } = useOrganizationTeamStore();
  const [showAlreadyConfirmedModal, setShowAlreadyConfirmedModal] = useState(false);
  const [showRequestAuthenticationModal, setShowRequestAuthenticationModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showOwnershipVerificationModal, setShowOwnershipVerificationModal] = useState(false);
  const [showCreateOrganizationModal, setShowCreateOrganizationModal] = useState(false);
  const [showTeamRegistrationModal, setShowTeamRegistrationModal] = useState(false);
  const [teamAuthId, setTeamAuthId] = useState<string>('');
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string>('');
  const [currentOrganizationName, setCurrentOrganizationName] = useState<string>('');

  const { setShowToast, setToastInfos } = useToastStore();

  const defaultIsMobile = useMediaQuery('(max-width:768px)');
  const isMobile = propIsMobile !== undefined ? propIsMobile : defaultIsMobile;

  // 내 병원인지 확인
  const isMyStore = currentAppStore ? teams.some((team) => Number(team.storeId) === currentAppStore.storeId) : false;

  const registerTeam = async () => {
    if (!currentAppStore) return;

    const token = getToken();
    if (!token) {
      setShowRequestAuthenticationModal(true);
      return;
    }

    setLoading(true);
    try {
      const teamCheckResult = await checkTeam(currentAppStore.storeId);

      if (!teamCheckResult.isSuccess || !teamCheckResult.data) {
        setToastInfos({ text: teamCheckResult.message || '팀 정보 확인에 실패했습니다.' });
        setShowToast(true);
        return;
      }

      if (teamCheckResult.data.isAlreay) {
        setShowAlreadyConfirmedModal(true);
      } else {
        setShowOwnershipVerificationModal(true);
      }
    } catch (error) {
      console.error('팀 확인 중 오류 발생:', error);
      setToastInfos({ text: '팀 확인 중 오류가 발생했습니다.' });
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseOwnershipVerificationModal = () => {
    setShowOwnershipVerificationModal(false);
  };

  const handleOwnershipVerificationSuccess = async (authId: string) => {
    handleCloseOwnershipVerificationModal();
    setTeamAuthId(authId);

    setLoading(true);
    try {
      if (organizations && organizations.length > 0 && organizations[0].organizationId) {
        setCurrentOrganizationId(organizations[0].organizationId);
        setCurrentOrganizationName(organizations[0].organizationName);
        setShowTeamRegistrationModal(true);
      } else {
        setShowCreateOrganizationModal(true);
      }
    } catch (error) {
      console.error('조직 확인 중 오류 발생:', error);
      setToastInfos({ text: '조직 확인 중 오류가 발생했습니다.' });
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCreateOrganizationModal = () => {
    setShowCreateOrganizationModal(false);
  };

  const handleCreateOrganizationSuccess = async (orgData: { organizationId: string; organizationName: string }) => {
    handleCloseCreateOrganizationModal();

    setCurrentOrganizationId(orgData.organizationId);
    setCurrentOrganizationName(orgData.organizationName);

    // 토큰 갱신
    await refreshAccessToken();

    setShowTeamRegistrationModal(true);
  };

  const handleCloseTeamRegistrationModal = () => {
    setShowTeamRegistrationModal(false);
  };

  const handleTeamRegistrationSuccess = async (teamName: string) => {
    handleCloseTeamRegistrationModal();

    // 토큰 갱신
    await refreshAccessToken();

    if (onLoginSuccess) {
      onLoginSuccess();
    }

    // 페이지 리프레시
    window.location.reload();
  };

  const handleCloseAlreadyConfirmedModal = () => {
    setShowAlreadyConfirmedModal(false);
  };

  const handleCloseRequestAuthenticationModal = () => {
    setShowRequestAuthenticationModal(false);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // 내병원/관심 병원 UI 렌더링
  const renderStoreHeader = () => {
    if (isMobile) {
      return (
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'primary.main',
              mb: 0.5,
              display: 'block'
            }}
          >
            {isMyStore ? '내 병원' : '관심 병원'}
          </Typography>
          <Box
            onClick={handleMenuClick}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1
            }}
          >
            <Typography variant="h3">{currentAppStore?.storeName}</Typography>
            <KeyboardArrowDown
              sx={{
                fontSize: '24px',
                color: 'text.secondary',
                transition: 'transform 0.2s',
                transform: anchorEl ? 'rotate(180deg)' : 'none'
              }}
            />
          </Box>
          {isMyStore ? (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              총 {totalReviewCount}개의 리뷰
            </Typography>
          ) : (
            <Button variant="contained" size="small" onClick={registerTeam} disabled={loading} sx={{ mb: 1 }}>
              {loading ? '로딩중...' : '인증하기'}
            </Button>
          )}
        </Box>
      );
    }

    return (
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: 'primary.main',
            mb: 0.5,
            display: 'block'
          }}
        >
          {isMyStore ? '내 병원' : '관심 병원'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box
            onClick={handleMenuClick}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography variant="h2">{currentAppStore?.storeName}</Typography>
            <KeyboardArrowDown
              sx={{
                fontSize: '24px',
                color: 'text.secondary',
                transition: 'transform 0.2s',
                transform: anchorEl ? 'rotate(180deg)' : 'none'
              }}
            />
          </Box>
          {isMyStore ? (
            <Typography variant="h6" color="text.secondary">
              총 {totalReviewCount.toLocaleString('ko')}개의 리뷰
            </Typography>
          ) : (
            <Button variant="contained" size="small" onClick={registerTeam} disabled={loading}>
              {loading ? '로딩중...' : '인증하기'}
            </Button>
          )}
        </Box>
        {currentAppStore?.storeDetail && (
          <Typography variant="subtitle1" color="text.secondary">
            {currentAppStore.storeDetail}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      <FlexBox style={{ justifyContent: 'space-between', width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{renderStoreHeader()}</Box>
        {renderRightSection && renderRightSection()}
      </FlexBox>

      <HospitalMenu anchorEl={anchorEl} onClose={handleMenuClose} redirectToReviews={redirectToReviews} />

      {!isMyStore && currentAppStore && (
        <OwnershipVerificationModal
          showModal={showOwnershipVerificationModal}
          handleShowModal={handleCloseOwnershipVerificationModal}
          storeName={currentAppStore.storeName}
          storeId={currentAppStore.storeId}
          onLoginSuccess={handleOwnershipVerificationSuccess}
        />
      )}

      {currentAppStore && (
        <CreateOrganizationModal
          teamAuthId={teamAuthId}
          storeId={currentAppStore.storeId}
          storeName={currentAppStore.storeName}
          showModal={showCreateOrganizationModal}
          handleShowModal={handleCloseCreateOrganizationModal}
          onOrganizationCreated={handleCreateOrganizationSuccess}
        />
      )}

      {currentAppStore && (
        <TeamRegistrationModal
          showModal={showTeamRegistrationModal}
          handleShowModal={handleCloseTeamRegistrationModal}
          storeName={currentAppStore.storeName}
          storeId={currentAppStore.storeId}
          teamAuthId={teamAuthId}
          organizationId={currentOrganizationId}
          organizationName={currentOrganizationName}
          onRegisterComplete={handleTeamRegistrationSuccess}
        />
      )}

      {currentAppStore && <AlreadyConfirmedModal showModal={showAlreadyConfirmedModal} handleShowModal={handleCloseAlreadyConfirmedModal} storeName={currentAppStore.storeName} />}

      <RequestAuthenticationModal showModal={showRequestAuthenticationModal} handleShowModal={handleCloseRequestAuthenticationModal} type={RequireAuthType.AI_RECOMMEND_REPLY} />
    </>
  );
};

export default StoreAuthHandler;
